import { useState, useCallback, useEffect } from 'react';
import { formatNumber } from '@atc/string-fns';
import { Text, SideBarToModal, Button, DisclaimerText, Panel, ColoredBackground } from 'reaxl';
import { Input, Form, RadioGroup } from 'reaxl-forms';
import { calculatePersonalPayment } from '@atc/wallet-payment-fns';
import { useDevice } from '@atc/react-device';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
const CUSTOM_RATE_LABEL = 'Custom Rate';
const CUSTOM_RATE_REGEX = /^\d{1,2}(\.\d{1,2})?$/;
const renderPayment = payment => /*#__PURE__*/jsxs("div", {
  className: "margin-left-auto flex-shrink-0 flex-grow-0 padding-left-2",
  children: [/*#__PURE__*/jsx(Text, {
    size: 500,
    weight: "bold",
    className: "first-price",
    children: payment ? formatNumber({
      value: Math.floor(payment)
    }) : '- - -'
  }), /*#__PURE__*/jsx(Text, {
    size: 200,
    children: "/mo.*"
  })]
});
const renderLender = (name, rate) => /*#__PURE__*/jsxs("div", {
  children: [/*#__PURE__*/jsx(Text, {
    componentClass: "div",
    weight: "bold",
    children: name
  }), /*#__PURE__*/jsxs(Text, {
    children: ["Rate: ", rate, "%"]
  })]
});

/**
 *  Modal for displaying selectable list of lending options
 *
 */
function LendingOptionsModal(_ref) {
  let {
    container,
    // syndicated calc
    options = [],
    onSubmit = () => {},
    onHide = () => {},
    disclaimer = '',
    initialSelection = 0,
    initialCustomRate = '',
    customPaymentDetails = {},
    disableCustom = false,
    isVisible = true,
    secondaryCTA = {}
  } = _ref;
  const isValidInitialSelection = initialSelection >= 0 && initialSelection < options.length;
  if (!customPaymentDetails.price) disableCustom = true;
  const [customRatePayment, setCustomRatePayment] = useState(null);
  const [customRate, setCustomRate] = useState(initialCustomRate);
  const [rateSelection, setRateSelection] = useState(isValidInitialSelection ? options[initialSelection] : options[0]);
  const [isCustomRateValid, setIsCustomRateValid] = useState(false);
  const [isCustomRateSelected, setIsCustomRateSelected] = useState(!disableCustom && !!initialCustomRate && !isValidInitialSelection);
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;

  // Handler for Custom APR input field
  const handleCustomInput = useCallback(event => {
    const rate = event?.target?.value ?? null;
    let payment = null;
    if (CUSTOM_RATE_REGEX.test(rate)) {
      const {
        price,
        ...financeDetails
      } = customPaymentDetails;
      payment = calculatePersonalPayment(price, financeDetails, rate);
      setIsCustomRateValid(true);
      setCustomRate(rate);
      setCustomRatePayment(payment);
    } else {
      setIsCustomRateValid(false);
    }
  }, [customPaymentDetails]);
  useEffect(() => {
    // Immediately calculate payment with custom rate, recalc as needed
    if (!disableCustom) {
      if (initialCustomRate) {
        handleCustomInput({
          target: {
            value: initialCustomRate
          }
        });
      }
      if (initialSelection === -1) {
        setIsCustomRateSelected(true);
      } else {
        setIsCustomRateSelected(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCustomRate, initialSelection]);

  /*
   * Wrapper for call to action to pass selection payload and event
   * { name, rate, payment}
   */
  const handleSubmit = event => {
    onSubmit(isCustomRateSelected ? {
      name: CUSTOM_RATE_LABEL,
      rate: customRate,
      payment: customRatePayment
    } : rateSelection, event);
    onHide();
  };

  // Sets state values required for custom rate validation when needed
  // " " Value change also triggers form validation for visual feedback
  const toggleCustomValidation = function () {
    let enable = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (enable) {
      setCustomRate(customRate ?? ' ');
      setIsCustomRateSelected(true);
    } else {
      setCustomRate(customRate === ' ' ? '' : customRate);
      setIsCustomRateSelected(false);
    }
  };

  // Handler for RadioGroup options
  const onChange = event => {
    const selection = event?.target?.value;
    if (selection === 'custom') {
      toggleCustomValidation(true);
    } else {
      toggleCustomValidation();
      setRateSelection(JSON.parse(selection));
    }
  };
  const continueButton = /*#__PURE__*/jsx(Button, {
    onClick: handleSubmit,
    bsStyle: "primary",
    type: "button",
    disabled: !(!isCustomRateSelected || isCustomRateValid),
    children: "Continue"
  });
  const customOption = {
    label: /*#__PURE__*/jsxs("div", {
      className: "display-flex",
      children: [/*#__PURE__*/jsxs(Text, {
        componentClass: "div",
        weight: "bold",
        className: "padding-right-6 flex-grow-1",
        children: ["Custom Interest Rate", /*#__PURE__*/jsx(Input, {
          name: "lender-options-custom-apr",
          type: "number",
          step: ".01",
          onChange: handleCustomInput,
          validations: {
            matchRegexp: CUSTOM_RATE_REGEX
          },
          validationError: "Enter an interest rate less than 100%. For example, 17.9%",
          validateOn: "change",
          value: customRate
          // required to override .radio input styling
          ,

          className: "display-block"
        })]
      }), renderPayment(customRatePayment)]
    }),
    value: 'custom'
  };
  const createLendingOption = _ref2 => {
    let {
      name,
      rate,
      payment
    } = _ref2;
    return {
      label: /*#__PURE__*/jsxs("div", {
        className: "display-flex",
        children: [renderLender(name, rate), renderPayment(payment)]
      }),
      value: JSON.stringify({
        name,
        rate,
        payment
      })
    };
  };
  if (!options.length) return null;
  const radioItems = [...options.map(option => createLendingOption(option))];
  if (!disableCustom) radioItems.push(customOption);
  const renderSecondaryCTA = () => {
    const {
      ctaElement,
      onClick
    } = secondaryCTA;
    return /*#__PURE__*/jsx(Panel, {
      onClick: onClick,
      className: "cursor-pointer",
      children: /*#__PURE__*/jsx(ColoredBackground, {
        children: /*#__PURE__*/jsx(Panel.Body, {
          children: ctaElement
        })
      })
    });
  };
  return /*#__PURE__*/jsx(SideBarToModal, {
    container: container,
    title: "Lending Options",
    content: /*#__PURE__*/jsxs(Fragment, {
      children: [disclaimer && /*#__PURE__*/jsx(DisclaimerText, {
        children: disclaimer
      }), /*#__PURE__*/jsx(Form, {
        children: /*#__PURE__*/jsx(RadioGroup, {
          name: "lender-options",
          labelClassName: "margin-vertical-4",
          options: radioItems,
          onChange: onChange,
          value: isCustomRateSelected ? 'custom' : JSON.stringify(rateSelection)
        })
      }), /*#__PURE__*/jsx("hr", {}), /*#__PURE__*/jsx(DisclaimerText, {
        className: "margin-vertical-4",
        children: "*Est. payment based on vehicle price (excluding taxes and fees). Terms may vary."
      }), secondaryCTA?.ctaElement && renderSecondaryCTA()]
    }),
    footerContent: /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Button, {
        bsStyle: "link",
        onClick: onHide,
        children: "Cancel"
      }), continueButton]
    }),
    collapsed: isXs,
    show: isVisible,
    sideBarOptions: {
      fullWidth: true,
      action: continueButton
    },
    modalOptions: {
      bsStyle: 'sm'
    },
    onHide: onHide
  });
}
export { LendingOptionsModal as default };
